import React, { useEffect, useState } from "react";
import uploadIcon from "../../assets/upload_img.svg";
import { getRole } from "../../utils/GetRole";
import { saveContestResultThunk as onSaveContestResult } from "../../slices/contest/thunk";
import { getAuthUser } from "../../utils/GetAuthUser";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { Spinner } from "../../components/spinner/Spinner";

export const CountdownTimer = ({ timeLeft, contestId,isFinalResult }) => {
  const role = getRole();
  const dispatch = useDispatch();

  const [seconds, setSeconds] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [isUploadResultAllow, setIsUploadResultAllow] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const authUser = getAuthUser();

  // Redux selector
  const selectContestState = (state) => state.contest;
  const contestSelector = createSelector(selectContestState, (contest) => ({
    isLoading: contest.isLoading,
    isSaveResult: contest.isSaveResult,
  }));
  const { isLoading, isSaveResult } = useSelector(contestSelector);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setSelectedFile(file);
      setErrorMessage(""); // clear error if any
    }
  };

  const handleRemove = () => {
    setSelectedImage(null);
    setSelectedFile(null);
  };

  useEffect(() => {
    if (isSaveResult) {
      setIsUploadResultAllow(false);
      setSelectedFile(null);
      setSelectedImage(null);
    }
  }, [isSaveResult]);

  useEffect(() => {
    if (role === "artist") setIsUploadResultAllow(true);
    else setIsUploadResultAllow(false);
  }, [role]);

  useEffect(() => {
    setIsShowLoader(isLoading);
  }, [isLoading]);

  // Convert time string to seconds
  const parseTimeLeft = (timeStr) => {
    let days = 0,
      hrs = 0,
      mins = 0,
      secs = 0;
    if (!timeStr) return 0;
    const regex =
      /(?:(\d+)\s*day)?s?\s*(?:(\d+)\s*hr)?s?\s*(?:(\d+)\s*min)?s?\s*(?:(\d+)\s*sec)?s?/i;
    const match = timeStr.match(regex);
    if (match) {
      days = parseInt(match[1] || 0, 10);
      hrs = parseInt(match[2] || 0, 10);
      mins = parseInt(match[3] || 0, 10);
      secs = parseInt(match[4] || 0, 10);
    }
    return days * 86400 + hrs * 3600 + mins * 60 + secs;
  };

  // Countdown logic
  useEffect(() => {
    let totalSeconds = parseTimeLeft(timeLeft);
    setSeconds(totalSeconds);

    if (!totalSeconds) return;

    const interval = setInterval(() => {
      totalSeconds -= 1;
      if (totalSeconds <= 0) {
        clearInterval(interval);
        setSeconds(0);
      } else {
        setSeconds(totalSeconds);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  const formatCountdown = (seconds) => {
    if (seconds == null) return { d: 0, h: 0, m: 0, s: 0 };
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return { d, h, m, s };
  };

  // ✅ Validation and submission
  const handleSaveResult = async (e) => {
  e.preventDefault();

  if (!selectedFile) {
    setErrorMessage("Please upload an image before submitting.");
    return;
  }

  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
  if (!allowedTypes.includes(selectedFile.type)) {
    setErrorMessage("Only JPG, JPEG, and PNG files are allowed.");
    return;
  }

  if (selectedFile.size > 5 * 1024 * 1024) {
    setErrorMessage("File size should be less than 5MB.");
    return;
  }

  setErrorMessage("");

  const formData = new FormData();
  formData.append("artist_id", authUser?.user?.id);
  formData.append("contest_id", contestId);
  formData.append("media", selectedFile);
  formData.append("isFinalResult", isFinalResult ? 1 : 2);

  try {
    await dispatch(onSaveContestResult(formData)).unwrap();

    window.location.reload();

    // OR if you literally want full page reload
    // window.location.reload();

  } catch (error) {
    console.error("Failed to save result:", error);
  }
};


  return (
    <>
      <div className="flex items-center justify-center flex-col">
        <p className="text-[16px] font-medium font-grotesk text-black mb-4">
          Result will be show within
        </p>
        <h2 className="text-2xl sm:text-[42px] font-bold font-grotesk text-secondary text-center leading-tight">
          <div>
            {formatCountdown(seconds).d} Days {formatCountdown(seconds).h} Hrs
          </div>
          <div className="text-sm mt-1">
            {formatCountdown(seconds).m} Min {formatCountdown(seconds).s} Sec
          </div>
        </h2>
      </div>

      {isUploadResultAllow && (
        <div className="max-w-[614px] min-h-[127px] w-full mt-8 border border-dashed border-border_color rounded-[12px] flex items-center justify-center p-3">
          <form className="w-full" onSubmit={handleSaveResult}>
            <div className="w-full flex items-center justify-center mt-8">
              {!selectedImage ? (
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer text-gray-500 hover:text-secondary flex items-center gap-1"
                >
                  <span>
                    <img src={uploadIcon} alt="upload" />
                  </span>
                  <span className="text-black text-xs sm:text-[16px] font-medium font-grotesk">
                    Upload your image
                  </span>
                </label>
              ) : (
                <div className="relative w-32 h-32">
                  <img
                    src={selectedImage}
                    alt="preview"
                    className="w-full h-full object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute -top-2 -right-2 bg-white rounded-full px-2 py-[2px] shadow hover:bg-gray-200 flex items-center justify-center flex-shrink-0"
                  >
                    <span className="text-red-500 font-bold">X</span>
                  </button>
                </div>
              )}

              <input
                id="file-upload"
                name="media"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {errorMessage && (
              <p className="text-red-500 text-sm mt-3 text-center">
                {errorMessage}
              </p>
            )}

            <div className="w-full flex justify-end mt-4">
              <button
                type="submit"
                className="bg-primary text-white px-3 py-1 rounded-lg shadow hover:bg-primary/90 transition"
                disabled={isShowLoader}
              >
                {isShowLoader ? <Spinner /> : "Upload"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
